import { motion } from "framer-motion";
import {
  Popover,
  IconButton,
  Button,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CircleIcon from "@mui/icons-material/Circle";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useAtom } from "jotai";
import { themeAtom, currentStaffDataAtom } from "../-Atoms";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Clean, minimal button styling
const getButtonStyles = (theme, variant = "contained", size = "medium") => {
  const baseStyles = {
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 500,
    fontSize: size === "small" ? "0.8rem" : "0.875rem",
    padding: size === "small" ? "8px 16px" : "12px 24px",
    transition: "all 0.2s ease",
    minHeight: size === "small" ? "32px" : "40px",
    "&:hover": {
      transform: "none",
    },
    "&:active": {
      transform: "scale(0.98)",
    },
  };

  if (variant === "contained") {
    return {
      ...baseStyles,
      backgroundColor: theme === "dark" ? "#ffffff" : "#1976d2",
      color: theme === "dark" ? "#000000" : "#ffffff",
      border: "none",
      "&:hover": {
        backgroundColor: theme === "dark" ? "#f5f5f5" : "#1565c0",
      },
    };
  } else if (variant === "outlined") {
    return {
      ...baseStyles,
      backgroundColor: "transparent",
      border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"}`,
      color: theme === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.8)",
      "&:hover": {
        backgroundColor: theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
        borderColor: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
      },
    };
  }
};

// Clean form field styling
const getFormFieldStyles = (theme) => ({
  "& .MuiInputLabel-root": {
    color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
    fontWeight: 500,
    fontSize: "0.875rem",
    "&.MuiInputLabel-shrink": {
      color: theme === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.9)",
    },
  },
  "& .MuiFilledInput-root": {
    borderRadius: "8px",
    backgroundColor: theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
    border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
      borderColor: theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
    },
    "&.Mui-focused": {
      backgroundColor: theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      borderColor: theme === "dark" ? "#ffffff" : "#1976d2",
    },
  },
  "& .MuiInputBase-root": {
    borderRadius: "8px",
    "&.Mui-focused": {
      backgroundColor: theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
    },
  },
});

const FiltersPopover = ({
  anchorEl,
  open,
  onClose,
  isMobile,
  // Filter props
  queryFilter,
  setQueryFilter,
  clientData,
  staffData,
  studios,
  calendarColours,
  showStaff,
  setShowStaff,
  broadClientFilter,
  setBroadClientFilter,
  // Mobile menu props
  calendarRef,
  showWeekends,
  setShowWeekends,
  setExportDatesModalOpen,
}) => {
  const [currentStaffData] = useAtom(currentStaffDataAtom);
  const [theme] = useAtom(themeAtom);

  const resetFilters = () => {
    if (currentStaffData?.id) {
      if (currentStaffData.isUtility) {
        setQueryFilter(null);
        onClose();
        return;
      }
      setQueryFilter({ staff: [currentStaffData] });
      onClose();
      return;
    }
    setQueryFilter(null);
    onClose();
  };

  const clearFilters = () => {
    setQueryFilter(null);
    onClose();
  };

  const handleMobileAction = (action) => {
    action();
    onClose();
  };

  const outlinedButtonStyles = getButtonStyles(theme, "outlined");

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: "easeOut",
      },
    },
  };


  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: isMobile ? "center" : "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: isMobile ? "center" : "left",
      }}
      slotProps={{
        paper: {
          sx: {
            width: isMobile ? "95vw" : "auto",
            minWidth: isMobile ? "auto" : "720px",
            maxWidth: isMobile ? "95vw" : "800px",
            maxHeight: isMobile ? "90vh" : "none",
            overflow: "visible",
            borderRadius: "12px",
            backgroundColor: theme === "dark" ? "#1a1a1a" : "#ffffff",
            border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
          },
        },
      }}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          padding: 20,
          maxHeight: isMobile ? "calc(90vh - 40px)" : "80vh",
          overflowY: "auto",
        }}
      >
        {/* Header Section */}
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between", 
          marginBottom: 2,
          paddingBottom: 1.5,
          borderBottom: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600,
            color: theme === "dark" ? "#ffffff" : "#000000",
            fontSize: "1.1rem"
          }}>
            Filters
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Mobile Navigation Buttons */}
        {isMobile && (
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ 
              fontWeight: 500,
              marginBottom: 1.5,
              color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
            }}>
              Quick Actions
            </Typography>
            
            <Box sx={{ display: "flex", gap: 1, marginBottom: 2 }}>
              <Button
                sx={{ ...getButtonStyles(theme, "contained", "small"), flex: 1 }}
                variant="contained"
                onClick={() => handleMobileAction(() => calendarRef.current.getApi().today())}
              >
                Today
              </Button>
              <Button
                sx={{ ...getButtonStyles(theme, "outlined", "small"), flex: 1 }}
                variant="outlined"
                onClick={() => handleMobileAction(() => setShowWeekends(!showWeekends))}
              >
                Weekends
              </Button>
            </Box>
            
            <Box sx={{ display: "flex", gap: 0.5, marginBottom: 2 }}>
              <Button
                sx={{ ...getButtonStyles(theme, "outlined", "small"), flex: 1 }}
                variant="outlined"
                onClick={() => handleMobileAction(() => calendarRef.current.getApi().changeView("dayGridMonth"))}
              >
                Month
              </Button>
              <Button
                sx={{ ...getButtonStyles(theme, "outlined", "small"), flex: 1 }}
                variant="outlined"
                onClick={() => handleMobileAction(() => calendarRef.current.getApi().changeView("timeGridWeek"))}
              >
                Week
              </Button>
              <Button
                sx={{ ...getButtonStyles(theme, "outlined", "small"), flex: 1 }}
                variant="outlined"
                onClick={() => handleMobileAction(() => calendarRef.current.getApi().changeView("timeGridDay"))}
              >
                Day
              </Button>
            </Box>
          </Box>
        )}

        {/* Filters Section */}
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 16, marginBottom: 16 }}>
          <StaffSelector
            staffData={staffData}
            staff={queryFilter?.staff || []}
            queryFilter={queryFilter}
            setQueryFilter={setQueryFilter}
            isMobile={isMobile}
          />
          <CalendarSelector
            studios={studios}
            studio={queryFilter?.studio || []}
            queryFilter={queryFilter}
            setQueryFilter={setQueryFilter}
            calendarColours={calendarColours}
            theme={theme}
            isMobile={isMobile}
          />
          <ClientSelector
            clientData={clientData}
            client={queryFilter?.client || []}
            queryFilter={queryFilter}
            setQueryFilter={setQueryFilter}
            isMobile={isMobile}
          />
        </div>

        {/* Filter Action Buttons */}
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", justifyContent: "center" }}>
          <Button 
            sx={{ ...outlinedButtonStyles, fontSize: 12, padding: "8px 16px" }}
            variant="outlined" 
            onClick={resetFilters}
          >
            Filter My Jobs
          </Button>
          <Button 
            sx={{ ...outlinedButtonStyles, fontSize: 12, padding: "8px 16px" }}
            variant="outlined" 
            onClick={clearFilters}
          >
            Remove All Filters
          </Button>
        </div>

        {/* Filter Options */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showStaff}
                onChange={(e) => setShowStaff(e.target.checked)}
              />
            }
            label="Show Staff"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={broadClientFilter}
                onChange={(e) => setBroadClientFilter(e.target.checked)}
              />
            }
            label="Broad Client Filter"
          />
        </div>
      </motion.div>
    </Popover>
  );
};

const StaffSelector = ({ staffData, staff, queryFilter, setQueryFilter, isMobile }) => {
  const [theme] = useAtom(themeAtom);
  const employedStaff = staffData.filter((staff) => staff.currentlyEmployed);
  const formFieldStyles = getFormFieldStyles(theme);

  if (isMobile) {
    return (
      <Box
        sx={{
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 1.5,
        }}
      >
        <Autocomplete
          disablePortal
          sx={formFieldStyles}
          size="small"
          options={employedStaff}
          getOptionLabel={(option) => findStaff(option, employedStaff)}
          ListboxProps={{
            style: {
              maxHeight: "200px",
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Staff Filter"
              variant="filled"
              slotProps={{
                input: {
                  ...params.InputProps,
                  disableUnderline: true,
                },
              }}
            />
          )}
          value={staff[0] || null}
          onChange={(_, value) => {
            setQueryFilter({ ...queryFilter, staff: value ? [value] : [] });
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 300,
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor:
          theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow:
          theme === "dark"
            ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
            : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
        padding: 1.5,
      }}
    >
      <Autocomplete
        multiple
        disablePortal
        sx={formFieldStyles}
        size="small"
        disableCloseOnSelect
        disableClearable
        options={employedStaff}
        getOptionLabel={(option) => findStaff(option, employedStaff)}
        ListboxProps={{
          style: {
            maxHeight: "200px",
          },
        }}
        renderOption={(props, option, { selected }) => {
          return (
            <li {...props}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.firstName} {option.lastName}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="filled"
            slotProps={{
              input: {
                ...params.InputProps,
                disableUnderline: true,
              },
            }}
            label="Staff Filter"
          />
        )}
        value={staff || []}
        onChange={(_, value) => {
          setQueryFilter({ ...queryFilter, staff: [...value] });
        }}
      />
    </Box>
  );
};

const CalendarSelector = ({ studios, studio, queryFilter, setQueryFilter, calendarColours, theme, isMobile }) => {
  const formFieldStyles = getFormFieldStyles(theme);
  
  return (
    <Box
      sx={{
        width: isMobile ? "100%" : 300,
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor:
          theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow:
          theme === "dark"
            ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
            : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
        padding: 1.5,
      }}
    >
      <FormControl size="small" sx={{ ...formFieldStyles, width: "100%" }}>
        <InputLabel id="studio-calendar">Studio Filter</InputLabel>
        <Select
          variant="filled"
          disableUnderline
          labelId="studio-calendar-select"
          value={studio || []}
          label="Studio Filter"
          multiple
          onChange={(e) => {
            setQueryFilter({ ...queryFilter, studio: [...e.target.value] });
          }}
        >
          <MenuItem key={"All Studios"} value={"All Studios"}>
            All Studios
          </MenuItem>
          {studios.map((s) => (
            <MenuItem key={s} value={s}>
              <span style={{ display: "flex", alignItems: "center" }}>
                <CircleIcon
                  style={{
                    fill:
                      theme === "dark"
                        ? calendarColours[s]?.darkBackground?.replace(
                            /(#(?:[\da-fA-F]{6}|[\da-fA-F]{3}))[\da-fA-F]{2}/,
                            "$1"
                          ) || "#000000"
                        : calendarColours[s]?.background || "#000000",
                    marginRight: 10,
                  }}
                />
                {s}
              </span>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

const ClientSelector = ({ clientData, client, queryFilter, setQueryFilter, isMobile }) => {
  const [theme] = useAtom(themeAtom);
  const formFieldStyles = getFormFieldStyles(theme);
  
  return (
    <Box
      sx={{
        width: isMobile ? "100%" : 300,
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor:
          theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow:
          theme === "dark"
            ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
            : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
        padding: 1.5,
      }}
    >
      <Autocomplete
        multiple
        disablePortal
        sx={formFieldStyles}
        size="small"
        disableCloseOnSelect
        disableClearable
        options={clientData.map((c) => c.brand)}
        ListboxProps={{
          style: {
            maxHeight: "200px",
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="filled"
            slotProps={{
              input: {
                ...params.InputProps,
                disableUnderline: true,
              },
            }}
            label="Client Filter"
          />
        )}
        renderOption={(props, option, { selected }) => {
          return (
            <li {...props}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option}
            </li>
          );
        }}
        value={client || []}
        onChange={(_, value) => {
          setQueryFilter({ ...queryFilter, client: [...value] });
        }}
      />
    </Box>
  );
};

const findStaff = (option, staffData) => {
  if (!option) return "";
  if (option.firstName) return `${option.firstName} ${option.lastName}`;
  for (let i = 0; i < staffData.length; i++) {
    if (staffData[i].id === option) {
      return `${staffData[i].firstName} ${staffData[i].lastName}`;
    }
  }
  return option;
};

export default FiltersPopover;